/**
 * 主题管理工具
 *
 * 提供主题管理的核心逻辑：主题切换、持久化、DOM 更新、系统偏好检测等。
 *
 */

/**
 * 主题定义
 */
export interface ThemeDefinition {
  /** 主题 ID */
  id: string;
  /** 主题名称 */
  name: string;
  /** 是否为暗色主题 */
  isDark: boolean;
  /** 主色调 */
  primaryColor?: string;
  /** 自定义 CSS 变量 */
  cssVars?: Record<string, string>;
  /** antd 主题 token */
  token?: Record<string, unknown>;
}

/**
 * 主题管理器配置
 */
export interface ThemeManagerOptions {
  /** 可用的主题列表 */
  themes: ThemeDefinition[];
  /** 默认主题 ID */
  defaultThemeId?: string;
  /** 存储 key，默认 'app-theme' */
  storageKey?: string;
  /** 是否跟随系统暗色偏好，默认 false */
  followSystem?: boolean;
}

/**
 * 主题管理器
 */
export interface ThemeManager {
  /** 获取当前主题 */
  getCurrentTheme: () => ThemeDefinition;
  /** 获取当前主题 ID */
  getCurrentThemeId: () => string;
  /** 切换主题 */
  setTheme: (themeId: string) => void;
  /** 获取所有可用主题 */
  getThemes: () => ThemeDefinition[];
  /** 判断当前是否为暗色主题 */
  isDark: () => boolean;
  /** 切换明暗模式 */
  toggleDarkMode: () => void;
}

/**
 * 创建主题管理器
 *
 * @param options - 主题管理器配置
 * @returns 主题管理器实例
 *
 * @example
 * ```ts
 * const themeManager = createThemeManager({
 *   themes: [
 *     { id: 'light', name: '明亮', isDark: false, primaryColor: '#1677ff' },
 *     { id: 'dark', name: '暗色', isDark: true, primaryColor: '#1668dc' },
 *   ],
 *   defaultThemeId: 'light',
 *   followSystem: true,
 * });
 *
 * // 切换主题
 * themeManager.setTheme('dark');
 * // 获取当前主题
 * const theme = themeManager.getCurrentTheme();
 * ```
 */
export function createThemeManager(options: ThemeManagerOptions): ThemeManager {
  const {
    themes,
    defaultThemeId,
    storageKey = 'app-theme',
    followSystem = false,
  } = options;

  function getInitialThemeId(): string {
    // 1. 从存储中读取
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved && themes.some((t) => t.id === saved)) {
        return saved;
      }
    } catch {
      // localStorage 不可用
    }

    // 2. 跟随系统偏好
    if (followSystem && getSystemPrefersDark()) {
      const darkTheme = themes.find((t) => t.isDark);
      if (darkTheme) return darkTheme.id;
    }

    // 3. 使用默认值
    return defaultThemeId ?? themes[0]?.id ?? 'light';
  }

  let currentThemeId = getInitialThemeId();

  function findTheme(id: string): ThemeDefinition {
    return themes.find((t) => t.id === id) ?? themes[0];
  }

  function persistThemeId(id: string) {
    try {
      localStorage.setItem(storageKey, id);
    } catch {
      // localStorage 不可用
    }
  }

  return {
    getCurrentTheme: () => findTheme(currentThemeId),
    getCurrentThemeId: () => currentThemeId,
    setTheme: (themeId: string) => {
      if (!themes.some((t) => t.id === themeId)) return;
      currentThemeId = themeId;
      persistThemeId(themeId);
      const theme = findTheme(themeId);
      applyThemeToDom(theme);
    },
    getThemes: () => [...themes],
    isDark: () => findTheme(currentThemeId).isDark,
    toggleDarkMode: () => {
      const current = findTheme(currentThemeId);
      const target = themes.find((t) => t.isDark !== current.isDark);
      if (target) {
        currentThemeId = target.id;
        persistThemeId(target.id);
        applyThemeToDom(target);
      }
    },
  };
}

/**
 * 将主题应用到 DOM
 *
 * - 设置 `<html>` 的 `data-theme` 属性
 * - 设置 `<html>` 的 `class` （dark/light）
 * - 设置 CSS 变量
 *
 * @param theme - 主题定义
 */
export function applyThemeToDom(theme: ThemeDefinition): void {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;

  // 设置 data-theme 属性
  root.setAttribute('data-theme', theme.id);

  // 设置 dark/light class
  if (theme.isDark) {
    root.classList.add('dark');
    root.classList.remove('light');
  } else {
    root.classList.add('light');
    root.classList.remove('dark');
  }

  // 设置 CSS 变量
  if (theme.cssVars) {
    for (const [key, value] of Object.entries(theme.cssVars)) {
      const varName = key.startsWith('--') ? key : `--${key}`;
      root.style.setProperty(varName, value);
    }
  }

  // 设置 color-scheme
  root.style.colorScheme = theme.isDark ? 'dark' : 'light';
}

/**
 * 检测系统是否偏好暗色模式
 *
 * @returns 系统是否偏好暗色模式
 */
export function getSystemPrefersDark(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(prefers-color-scheme: dark)')?.matches ?? false;
}

/**
 * 判断主题 ID 是否对应暗色主题
 *
 * @param themeId - 主题 ID
 * @param themes - 主题列表
 * @returns 是否为暗色主题
 */
export function isDarkTheme(
  themeId: string,
  themes: ThemeDefinition[],
): boolean {
  return themes.find((t) => t.id === themeId)?.isDark ?? false;
}

/**
 * 监听系统暗色模式变化
 *
 * @param callback - 变化回调
 * @returns 取消监听的函数
 *
 * @example
 * ```ts
 * const unwatch = watchSystemDarkMode((isDark) => {
 *   themeManager.setTheme(isDark ? 'dark' : 'light');
 * });
 *
 * // 清理
 * unwatch();
 * ```
 */
export function watchSystemDarkMode(
  callback: (isDark: boolean) => void,
): () => void {
  if (typeof window === 'undefined') return () => {};

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  const handler = (e: MediaQueryListEvent) => callback(e.matches);
  mediaQuery.addEventListener('change', handler);

  return () => mediaQuery.removeEventListener('change', handler);
}
